import React from "react";
import { Link } from "react-router-dom";
import { TrendingUp, ArrowLeft } from "lucide-react";
import { Button } from "@/components/ui/button";

const Privacy = () => {
  return (
    <div className="min-h-screen bg-[#0A0A0A]">
      {/* Navigation */}
      <nav className="border-b border-border">
        <div className="max-w-4xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <TrendingUp className="w-6 h-6" />
            <span className="font-bold">Aurex AI</span>
          </Link>
          <Link to="/">
            <Button variant="ghost" size="sm">
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Home
            </Button>
          </Link>
        </div>
      </nav>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-6 py-12">
        <h1 className="text-4xl font-black mb-2">Privacy Policy</h1>
        <p className="text-muted-foreground mb-8">Last updated: January 2025</p>

        <div className="prose prose-invert prose-sm max-w-none space-y-8">
          {/* Introduction */}
          <section>
            <h2 className="text-xl font-bold text-white mb-4">1. Introduction</h2>
            <p className="text-muted-foreground leading-relaxed">
              Aurex AI ("Company", "we", "our", "us") is committed to protecting your privacy. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you use our website aurexai.cloud and our trading signal services (the "Service").
            </p>
            <p className="text-muted-foreground leading-relaxed mt-4">
              Please read this Privacy Policy carefully. By accessing or using our Service, you acknowledge that you have read, understood, and agree to be bound by this Privacy Policy.
            </p>
          </section>

          {/* Information We Collect */}
          <section>
            <h2 className="text-xl font-bold text-white mb-4">2. Information We Collect</h2>
            
            <h3 className="text-lg font-semibold text-white mt-6 mb-3">2.1 Personal Information</h3>
            <p className="text-muted-foreground leading-relaxed">
              We collect information that you voluntarily provide when registering for or using our Service:
            </p>
            <ul className="list-disc pl-6 text-muted-foreground space-y-2 mt-4">
              <li><strong className="text-white">Account Information:</strong> Name, email address, password (encrypted)</li>
              <li><strong className="text-white">Profile Information:</strong> Profile picture (if using Google Sign-In)</li>
              <li><strong className="text-white">Payment Information:</strong> Transaction hashes, wallet addresses for cryptocurrency payments</li>
              <li><strong className="text-white">Communication Data:</strong> Information you provide when contacting support</li>
            </ul>

            <h3 className="text-lg font-semibold text-white mt-6 mb-3">2.2 Automatically Collected Information</h3>
            <p className="text-muted-foreground leading-relaxed">
              When you access our Service, we automatically collect certain information:
            </p>
            <ul className="list-disc pl-6 text-muted-foreground space-y-2 mt-4">
              <li><strong className="text-white">Device Information:</strong> Browser type, operating system, device type</li>
              <li><strong className="text-white">Log Data:</strong> IP address, access times, pages viewed, referring URLs</li>
              <li><strong className="text-white">Usage Data:</strong> Features used, signals generated, subscription status</li>
              <li><strong className="text-white">Cookies and Tracking:</strong> Session tokens, authentication data</li>
            </ul>

            <h3 className="text-lg font-semibold text-white mt-6 mb-3">2.3 Information from Third Parties</h3>
            <p className="text-muted-foreground leading-relaxed">
              If you choose to sign in using Google, we receive:
            </p>
            <ul className="list-disc pl-6 text-muted-foreground space-y-2 mt-4">
              <li>Your Google account email address</li>
              <li>Your name as displayed on Google</li>
              <li>Your profile picture URL</li>
            </ul>
          </section>

          {/* How We Use Information */}
          <section>
            <h2 className="text-xl font-bold text-white mb-4">3. How We Use Your Information</h2>
            <p className="text-muted-foreground leading-relaxed">
              We use the collected information for the following purposes:
            </p>
            <ul className="list-disc pl-6 text-muted-foreground space-y-2 mt-4">
              <li><strong className="text-white">Service Delivery:</strong> To provide, maintain, and improve our trading signal services</li>
              <li><strong className="text-white">Account Management:</strong> To create and manage your account, authenticate logins</li>
              <li><strong className="text-white">Payment Processing:</strong> To verify cryptocurrency payments and manage subscriptions</li>
              <li><strong className="text-white">Communication:</strong> To send transactional emails (payment confirmations, subscription updates, password resets)</li>
              <li><strong className="text-white">Customer Support:</strong> To respond to your inquiries and provide assistance</li>
              <li><strong className="text-white">Analytics:</strong> To understand usage patterns and improve our Service</li>
              <li><strong className="text-white">Security:</strong> To detect and prevent fraud, abuse, and security incidents</li>
              <li><strong className="text-white">Legal Compliance:</strong> To comply with applicable laws and regulations</li>
            </ul>
          </section>

          {/* Data Sharing */}
          <section>
            <h2 className="text-xl font-bold text-white mb-4">4. How We Share Your Information</h2>
            <p className="text-muted-foreground leading-relaxed">
              We do not sell your personal information. We may share your information in the following circumstances:
            </p>
            
            <h3 className="text-lg font-semibold text-white mt-6 mb-3">4.1 Service Providers</h3>
            <p className="text-muted-foreground leading-relaxed">
              We share information with third-party service providers who perform services on our behalf:
            </p>
            <ul className="list-disc pl-6 text-muted-foreground space-y-2 mt-4">
              <li><strong className="text-white">Email Services:</strong> For sending transactional emails (e.g., Resend)</li>
              <li><strong className="text-white">Authentication:</strong> Google OAuth for social login</li>
              <li><strong className="text-white">Hosting:</strong> Cloud infrastructure providers</li>
              <li><strong className="text-white">Analytics:</strong> Usage analytics services</li>
            </ul>

            <h3 className="text-lg font-semibold text-white mt-6 mb-3">4.2 Legal Requirements</h3>
            <p className="text-muted-foreground leading-relaxed">
              We may disclose your information if required by law or in response to:
            </p>
            <ul className="list-disc pl-6 text-muted-foreground space-y-2 mt-4">
              <li>Legal process (subpoenas, court orders)</li>
              <li>Government requests</li>
              <li>To protect our rights, privacy, safety, or property</li>
              <li>To enforce our Terms of Service</li>
            </ul>

            <h3 className="text-lg font-semibold text-white mt-6 mb-3">4.3 Business Transfers</h3>
            <p className="text-muted-foreground leading-relaxed">
              In the event of a merger, acquisition, or sale of assets, your information may be transferred as part of that transaction.
            </p>
          </section>

          {/* Data Security */}
          <section>
            <h2 className="text-xl font-bold text-white mb-4">5. Data Security</h2>
            <p className="text-muted-foreground leading-relaxed">
              We implement appropriate technical and organizational security measures to protect your information:
            </p>
            <ul className="list-disc pl-6 text-muted-foreground space-y-2 mt-4">
              <li><strong className="text-white">Encryption:</strong> Passwords are hashed using bcrypt; data transmitted via HTTPS/TLS</li>
              <li><strong className="text-white">Access Controls:</strong> Limited access to personal data on a need-to-know basis</li>
              <li><strong className="text-white">Secure Infrastructure:</strong> Hosted on secure cloud infrastructure with monitoring</li>
              <li><strong className="text-white">Authentication:</strong> JWT tokens for session management with expiration</li>
            </ul>
            <p className="text-muted-foreground leading-relaxed mt-4">
              However, no method of transmission over the Internet or electronic storage is 100% secure. While we strive to protect your information, we cannot guarantee absolute security.
            </p>
          </section>

          {/* Data Retention */}
          <section>
            <h2 className="text-xl font-bold text-white mb-4">6. Data Retention</h2>
            <p className="text-muted-foreground leading-relaxed">
              We retain your information for as long as necessary to:
            </p>
            <ul className="list-disc pl-6 text-muted-foreground space-y-2 mt-4">
              <li>Provide our Service and maintain your account</li>
              <li>Comply with legal obligations</li>
              <li>Resolve disputes and enforce agreements</li>
              <li>Maintain business records for accounting and tax purposes</li>
            </ul>
            <p className="text-muted-foreground leading-relaxed mt-4">
              <strong className="text-white">Account Data:</strong> Retained while your account is active and for up to 3 years after deletion request for legal compliance.
            </p>
            <p className="text-muted-foreground leading-relaxed mt-2">
              <strong className="text-white">Payment Records:</strong> Retained for 7 years for financial and tax compliance.
            </p>
            <p className="text-muted-foreground leading-relaxed mt-2">
              <strong className="text-white">Log Data:</strong> Retained for up to 12 months for security and analytics purposes.
            </p>
          </section>

          {/* Cookies */}
          <section>
            <h2 className="text-xl font-bold text-white mb-4">7. Cookies and Tracking Technologies</h2>
            <p className="text-muted-foreground leading-relaxed">
              We use cookies and similar technologies to:
            </p>
            <ul className="list-disc pl-6 text-muted-foreground space-y-2 mt-4">
              <li><strong className="text-white">Essential Cookies:</strong> Required for authentication and basic functionality</li>
              <li><strong className="text-white">Session Cookies:</strong> To maintain your login session</li>
              <li><strong className="text-white">Preference Cookies:</strong> To remember your settings and preferences</li>
            </ul>
            <p className="text-muted-foreground leading-relaxed mt-4">
              You can control cookies through your browser settings. Disabling essential cookies may affect Service functionality.
            </p>
          </section>

          {/* Your Rights */}
          <section>
            <h2 className="text-xl font-bold text-white mb-4">8. Your Rights and Choices</h2>
            <p className="text-muted-foreground leading-relaxed">
              Depending on your location, you may have the following rights regarding your personal information:
            </p>
            
            <h3 className="text-lg font-semibold text-white mt-6 mb-3">8.1 Access and Portability</h3>
            <p className="text-muted-foreground leading-relaxed">
              You can access your account information at any time by logging into your account. You may request a copy of your personal data in a portable format.
            </p>

            <h3 className="text-lg font-semibold text-white mt-6 mb-3">8.2 Correction</h3>
            <p className="text-muted-foreground leading-relaxed">
              You can update your account information directly through your account settings, or contact us to correct inaccurate data.
            </p>

            <h3 className="text-lg font-semibold text-white mt-6 mb-3">8.3 Deletion</h3>
            <p className="text-muted-foreground leading-relaxed">
              You may request deletion of your account and personal data. Note that some data may be retained for legal compliance as described in Section 6.
            </p>

            <h3 className="text-lg font-semibold text-white mt-6 mb-3">8.4 Marketing Communications</h3>
            <p className="text-muted-foreground leading-relaxed">
              You can opt out of promotional emails by clicking the "unsubscribe" link. Transactional emails (payment confirmations, security alerts) cannot be opted out of.
            </p>

            <h3 className="text-lg font-semibold text-white mt-6 mb-3">8.5 Do Not Track</h3>
            <p className="text-muted-foreground leading-relaxed">
              Our Service does not currently respond to "Do Not Track" browser signals.
            </p>
          </section>

          {/* International Transfers */}
          <section>
            <h2 className="text-xl font-bold text-white mb-4">9. International Data Transfers</h2>
            <p className="text-muted-foreground leading-relaxed">
              Your information may be transferred to and processed in countries other than your country of residence. These countries may have different data protection laws. By using our Service, you consent to the transfer of your information to these countries.
            </p>
            <p className="text-muted-foreground leading-relaxed mt-4">
              We take appropriate safeguards to ensure your data receives adequate protection in accordance with this Privacy Policy.
            </p>
          </section>

          {/* Children's Privacy */}
          <section>
            <h2 className="text-xl font-bold text-white mb-4">10. Children's Privacy</h2>
            <p className="text-muted-foreground leading-relaxed">
              Our Service is not intended for individuals under 18 years of age. We do not knowingly collect personal information from children. If you become aware that a child has provided us with personal information, please contact us, and we will take steps to delete such information.
            </p>
          </section>

          {/* Third-Party Links */}
          <section>
            <h2 className="text-xl font-bold text-white mb-4">11. Third-Party Links</h2>
            <p className="text-muted-foreground leading-relaxed">
              Our Service may contain links to third-party websites or services (e.g., trading platforms, brokers). We are not responsible for the privacy practices of these third parties. We encourage you to review their privacy policies before providing any personal information.
            </p>
          </section>

          {/* GDPR Compliance */}
          <section>
            <h2 className="text-xl font-bold text-white mb-4">12. European Users (GDPR)</h2>
            <p className="text-muted-foreground leading-relaxed">
              If you are located in the European Economic Area (EEA), you have additional rights under the General Data Protection Regulation (GDPR):
            </p>
            <ul className="list-disc pl-6 text-muted-foreground space-y-2 mt-4">
              <li><strong className="text-white">Legal Basis:</strong> We process your data based on contract performance (providing services), legitimate interests (security, analytics), and consent (where applicable)</li>
              <li><strong className="text-white">Right to Object:</strong> You can object to processing based on legitimate interests</li>
              <li><strong className="text-white">Right to Restrict:</strong> You can request restriction of processing in certain circumstances</li>
              <li><strong className="text-white">Supervisory Authority:</strong> You have the right to lodge a complaint with your local data protection authority</li>
            </ul>
          </section>

          {/* California Privacy */}
          <section>
            <h2 className="text-xl font-bold text-white mb-4">13. California Residents (CCPA)</h2>
            <p className="text-muted-foreground leading-relaxed">
              If you are a California resident, you have the following rights under the California Consumer Privacy Act (CCPA):
            </p>
            <ul className="list-disc pl-6 text-muted-foreground space-y-2 mt-4">
              <li><strong className="text-white">Right to Know:</strong> Request disclosure of personal information collected</li>
              <li><strong className="text-white">Right to Delete:</strong> Request deletion of personal information</li>
              <li><strong className="text-white">Right to Non-Discrimination:</strong> We will not discriminate against you for exercising your rights</li>
              <li><strong className="text-white">No Sale of Personal Information:</strong> We do not sell your personal information</li>
            </ul>
          </section>

          {/* Changes to Policy */}
          <section>
            <h2 className="text-xl font-bold text-white mb-4">14. Changes to This Privacy Policy</h2>
            <p className="text-muted-foreground leading-relaxed">
              We may update this Privacy Policy from time to time. We will notify you of any material changes by:
            </p>
            <ul className="list-disc pl-6 text-muted-foreground space-y-2 mt-4">
              <li>Posting the new Privacy Policy on this page</li>
              <li>Updating the "Last updated" date</li>
              <li>Sending an email notification for significant changes</li>
            </ul>
            <p className="text-muted-foreground leading-relaxed mt-4">
              Your continued use of the Service after any changes constitutes acceptance of the updated Privacy Policy.
            </p>
          </section>

          {/* Contact */}
          <section>
            <h2 className="text-xl font-bold text-white mb-4">15. Contact Us</h2>
            <p className="text-muted-foreground leading-relaxed">
              If you have any questions about this Privacy Policy, your data, or wish to exercise your rights, please contact us:
            </p>
            <div className="mt-4 p-4 bg-secondary/30 border border-border">
              <p className="text-white font-medium">Aurex AI - Privacy Team</p>
              <p className="text-muted-foreground">Email: admin@aurexai.cloud</p>
              <p className="text-muted-foreground">Website: https://aurexai.cloud</p>
            </div>
            <p className="text-muted-foreground leading-relaxed mt-4">
              We will respond to your request within 30 days.
            </p>
          </section>

          {/* Acknowledgment */}
          <section className="border-t border-border pt-8">
            <p className="text-muted-foreground leading-relaxed">
              By using Aurex AI, you acknowledge that you have read and understood this Privacy Policy and agree to the collection and use of your information as described herein.
            </p>
          </section>
        </div>

        {/* Back to top */}
        <div className="mt-12 pt-8 border-t border-border flex justify-between items-center">
          <Link to="/terms" className="text-sm text-muted-foreground hover:text-white transition-colors">
            ← Terms and Conditions
          </Link>
          <Link to="/">
            <Button variant="outline" size="sm">
              Back to Home
            </Button>
          </Link>
        </div>
      </div>

      {/* Footer */}
      <footer className="border-t border-border py-8 px-6">
        <div className="max-w-4xl mx-auto text-center">
          <p className="text-xs text-muted-foreground">
            © 2025 Aurex AI. All rights reserved.
          </p>
        </div>
      </footer>
    </div>
  );
};

export default Privacy;
